import type { Context } from '@fortune-sheet/core';
import { getSheetIndex, setFormulaCellInfoMap } from '@fortune-sheet/core';
import isEmpty from 'lodash/isEmpty';
import type { FortuneCell, FortuneSheet } from './types';
import { createFormulaLinkJob, createFormulaMapBuildJob } from './excel/workbookInternals';
import type { FormulaCellInfoMap } from './excel/workbookInternals';
import { deepFreezePlain } from './deepFreeze';

export { deepFreezePlain };

// Background work runs in slices this long, then yields so a click or key press is handled
// within a frame. Well under the 50 ms long-task line even on a CPU six times slower than an M2.
export const HYDRATE_SLICE_MS = 8;

const ROWS_PER_STEP = 512;
const CELLS_PER_STEP = 4096;
const FORMULAS_PER_STEP = 64;

/** Freezes a sheet's payload before it reaches <Workbook>, so the mount's immer pass skips it
 *  instead of walking it. The mount's commit freezes all of it but the celldata list anyway
 *  (that list never reaches the workbook state), and nothing changes a parsed celldata list after
 *  the converters build it. The sheet object itself stays open. */
export const freezeSheetPayload = (sheet: FortuneSheet): FortuneSheet => {
    Object.entries(sheet).forEach(([key, value]) => {
        if (key !== 'data') deepFreezePlain(value);
    });
    return sheet;
};

/** Runs `step` in HYDRATE_SLICE_MS slices, one task each, until it returns true, then `onDone`
 *  in the same task. Stops without calling `onDone` once `isStale()`. */
export const runInSlices = (step: () => boolean, onDone: () => void, isStale: () => boolean): void => {
    const slice = () => {
        if (isStale()) return;
        const end = performance.now() + HYDRATE_SLICE_MS;
        let finished = step();
        while (!finished && performance.now() < end) finished = step();
        if (finished) onDone();
        else setTimeout(slice, 0);
    };
    setTimeout(slice, 0);
};

export type CellMatrix = (FortuneCell | null)[][];
type FormulaCell = { r: number; c: number; id: FortuneSheet['id'] };

// setFormulaCellInfo parses these through isFunctionRange, which writes to the context itself;
// they register inside the commit's state update, as updateSheet registers them today.
const RANGE_FUNCTION = /INDIRECT\(|OFFSET\(|INDEX\(/i;

export interface PreparedSheet {
    /** Dense grid, frozen: exactly what updateSheet's initSheetData builds from the celldata. */
    data: CellMatrix;
    /** Formula cells in celldata order, as updateSheet registers them. */
    formulaCells: FormulaCell[];
    rangeFunctionCells: FormulaCell[];
    /** formulaCells' dependency entries and the reference-cache entries their parse added, worked
     *  out ahead against the prepared grid. Null when the workbook's formula map was empty then. */
    registered: { entries: Record<string, unknown>; cache: Record<string, unknown> } | null;
}

/**
 * Prepares a pending sheet for hydration a step at a time, off the state path: the dense grid
 * updateSheet's celldata path builds (size from the celldata and the stored row/column, else the
 * workbook's default grid; later cells win), every row and cell frozen as a commit would freeze
 * them, and the formula dependencies updateSheet would register. `step()` returns true once
 * `prepared()` is ready.
 */
export const createSheetBuild = (sheet: FortuneSheet, getContext: () => Context | undefined) => {
    const cells = sheet.celldata ?? [];
    let phase: 'size' | 'rows' | 'place' | 'formulas' | 'freeze' | 'register' | 'done' = 'size';
    let cursor = 0;
    let maxR = 0;
    let maxC = 0;
    let rows = 0;
    let cols = 0;
    const data: CellMatrix = [];
    const formulaCells: FormulaCell[] = [];
    const rangeFunctionCells: FormulaCell[] = [];
    let registered: PreparedSheet['registered'] = null;
    let registerCtx: Context | null = null;

    const next = (to: typeof phase) => {
        phase = to;
        cursor = 0;
    };

    const steps = {
        size: () => {
            const end = Math.min(cells.length, cursor + CELLS_PER_STEP);
            for (; cursor < end; cursor++) {
                if (cells[cursor].r > maxR) maxR = cells[cursor].r;
                if (cells[cursor].c > maxC) maxC = cells[cursor].c;
            }
            if (cursor < cells.length) return;
            const ctx = getContext();
            const { row, column } = sheet;
            const hasSize = row != null && column != null && row > 0 && column > 0;
            rows = Math.max(maxR + 1, hasSize ? row : ctx?.defaultrowNum ?? 0);
            cols = Math.max(maxC + 1, hasSize ? column : ctx?.defaultcolumnNum ?? 0);
            next('rows');
        },
        rows: () => {
            const end = Math.min(rows, cursor + ROWS_PER_STEP);
            for (; cursor < end; cursor++) data.push(new Array(cols).fill(null));
            if (cursor >= rows) next('place');
        },
        place: () => {
            const end = Math.min(cells.length, cursor + CELLS_PER_STEP);
            for (; cursor < end; cursor++) data[cells[cursor].r][cells[cursor].c] = cells[cursor].v as FortuneCell | null;
            if (cursor >= cells.length) next('formulas');
        },
        formulas: () => {
            const end = Math.min(cells.length, cursor + CELLS_PER_STEP);
            for (; cursor < end; cursor++) {
                const { r, c } = cells[cursor];
                const f = data[r][c]?.f;
                if (f != null) (RANGE_FUNCTION.test(String(f)) ? rangeFunctionCells : formulaCells).push({ r, c, id: sheet.id });
            }
            if (cursor >= cells.length) next('freeze');
        },
        freeze: () => {
            const end = Math.min(rows, cursor + ROWS_PER_STEP);
            for (; cursor < end; cursor++) {
                data[cursor].forEach((cell) => deepFreezePlain(cell));
                Object.freeze(data[cursor]);
            }
            if (cursor < rows) return;
            Object.freeze(data);
            next('register');
        },
        register: () => {
            if (!registerCtx) {
                // An empty map is left for Fortune's lazy rebuild (see the commit), so there is
                // nothing to register into. Otherwise parse against a copy of the live context
                // whose copy of this sheet holds the prepared grid, into private maps.
                const live = getContext();
                if (!formulaCells.length || !live || isEmpty(live.formulaCache.formulaCellInfoMap)) {
                    next('done');
                    return;
                }
                registered = { entries: {}, cache: Object.create(live.formulaCache.cellTextToIndexList ?? null) };
                registerCtx = {
                    ...live,
                    luckysheetfile: live.luckysheetfile.map((file) => (file.id === sheet.id ? { ...file, data } : file)),
                    formulaCache: { formulaCellInfoMap: registered.entries, cellTextToIndexList: registered.cache },
                } as unknown as Context;
            }
            const chunk = formulaCells.slice(cursor, cursor + FORMULAS_PER_STEP);
            cursor += chunk.length;
            setFormulaCellInfoMap(registerCtx, chunk, data);
            if (cursor >= formulaCells.length) next('done');
        },
    };

    return {
        step: (): boolean => {
            if (phase !== 'done') steps[phase]();
            return phase === 'done';
        },
        prepared: (): PreparedSheet => ({ data, formulaCells, rangeFunctionCells, registered }),
    };
};

/**
 * updateSheet's celldata path (initSheetData + per-cell formula registration) with the heavy part
 * already done by createSheetBuild. With `keepFormulaMap` false the caller clears the map
 * afterwards, as it does today, so nothing is registered. Returns false when the sheet is gone,
 * so the caller can take updateSheet's own path.
 */
export const commitPreparedSheet = (ctx: Context, sheet: FortuneSheet, prepared: PreparedSheet, keepFormulaMap: boolean): boolean => {
    if (sheet.id == null) return false;
    const index = getSheetIndex(ctx, sheet.id);
    const file = index == null ? undefined : ctx.luckysheetfile[index];
    if (!file) return false;
    file.data = prepared.data;
    delete file.celldata;
    const cache = ctx.formulaCache;
    const map = cache.formulaCellInfoMap as Record<string, unknown> | null;
    if (!keepFormulaMap || !map) return true;
    // Entries left from formulas typed into the stub; the hydrated grid replaced those cells.
    const sheetId = String(sheet.id);
    Object.keys(map).forEach((key) => {
        if (/^r\d+c\d+i(.*)$/.exec(key)?.[1] === sheetId) delete map[key];
    });
    if (prepared.registered) {
        Object.assign(map, prepared.registered.entries);
        cache.cellTextToIndexList = Object.assign(cache.cellTextToIndexList ?? {}, prepared.registered.cache);
    } else {
        setFormulaCellInfoMap(ctx, prepared.formulaCells, prepared.data);
    }
    setFormulaCellInfoMap(ctx, prepared.rangeFunctionCells, prepared.data);
    return true;
};

const preparing = new WeakSet<object>();

// What a formula map build reads, per sheet: if any of it is a different object by install time, the build is stale.
const formulaSources = (ctx: Context): unknown[] =>
    ctx.luckysheetfile.flatMap((file) => [file.id, file.name, file.data, file.calcChain, file.dynamicArray_compute]);

const sameSources = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * After a workbook settles, prepares in slices what its first edit would otherwise build inside that edit's state
 * update: the formula map when it is empty (the 830k-cell CEM file: ~0.7 s on an M2, ~3 s at 4x CPU) and the
 * dependency links (another ~0.2 s / ~1 s). The map is installed only if the sheets, grids and calcChains it read are
 * still the same objects and no edit has built one meanwhile; otherwise the first edit builds it as before.
 */
export const prepareFormulasInSlices = ({
    getContext,
    isStale,
}: {
    getContext: () => Context | undefined;
    isStale: () => boolean;
}): void => {
    const start = getContext();
    if (!start?.formulaCache || !Array.isArray(start.luckysheetfile) || !createFormulaMapBuildJob || !createFormulaLinkJob) return;
    const formulaCache = start.formulaCache;
    // One preparation per workbook at a time (its formula cache is shared by every state it goes through).
    if (preparing.has(formulaCache)) return;
    preparing.add(formulaCache);
    const done = () => preparing.delete(formulaCache);
    const abandoned = (stale: boolean) => {
        if (stale) done();
        return stale;
    };
    const link = (map: FormulaCellInfoMap) => {
        const job = createFormulaLinkJob!(map);
        runInSlices(job.step, done, () => abandoned(isStale() || getContext()?.formulaCache?.formulaCellInfoMap !== map));
    };
    const existing = formulaCache.formulaCellInfoMap;
    if (existing && !isEmpty(existing)) {
        link(existing);
        return;
    }
    if (!start.luckysheetfile.some((file) => file.calcChain?.length)) {
        done();
        return;
    }
    const sources = formulaSources(start);
    let build: ReturnType<NonNullable<typeof createFormulaMapBuildJob>>;
    try {
        build = createFormulaMapBuildJob(start);
    } catch {
        done();
        return;
    }
    let failed = false;
    const step = () => {
        try {
            return build.step();
        } catch {
            // e.g. a calcChain entry outside its grid: the edit's own rebuild meets the same cell
            failed = true;
            return true;
        }
    };
    runInSlices(
        step,
        () => {
            const result = failed ? null : build.result();
            const live = getContext();
            if (
                !result ||
                !live?.formulaCache ||
                !isEmpty(live.formulaCache.formulaCellInfoMap) ||
                !sameSources(formulaSources(live), sources)
            ) {
                done();
                return;
            }
            live.formulaCache.formulaCellInfoMap = result.map;
            live.formulaCache.cellTextToIndexList = Object.assign(live.formulaCache.cellTextToIndexList ?? {}, result.cache);
            link(result.map);
        },
        () => abandoned(isStale()),
    );
};
